'use client';

import { CONFIG } from '@frameio/config';
import { appComponentsStorage } from '../../storage';
import {
  type TooltipDefinition,
  type TooltipGroup,
  TOOLTIPS,
} from '../constants/tooltips';
import { isConfigEligibleForRoute } from '../utils/routeMatching';
import {
  applySelection,
  areAllTooltipsDismissedIn,
  findAllReadyTooltipsForRoute,
  isFeatureEnabled,
  ONBOARDING_FEATURE_ID,
  selectIsEligible,
} from '../utils/tooltipStoreSelectors';
import type {
  PersistedTooltipState,
  TooltipAudienceContext,
  TooltipConfig,
  TooltipContentOverrides,
  TooltipDismissalMethod,
  TooltipState,
  TooltipStoreState,
} from './tooltipStore.types';

const STORAGE_KEY = 'frame-io--onboarding-tooltips';
const RETRY_COOLDOWN_MS = 1500;
const MAX_RELEASE_RETRY_ATTEMPTS = 3;

const DEFAULT_PERSISTED_TOOLTIP_STATE: PersistedTooltipState = {
  tooltipsEnabled: false,
  dismissedIds: [],
  visitCounts: {},
  displayCounts: {},
};

type FlattenedTooltip = {
  id: string;
  config: TooltipConfig;
  groupId?: string;
};

function flattenTooltipGroups(tooltips: (TooltipDefinition | TooltipGroup)[]): {
  flattened: FlattenedTooltip[];
  multiStepGroups: Map<string, string[]>;
} {
  const flattened: FlattenedTooltip[] = [];
  const multiStepGroups = new Map<string, string[]>();

  for (const item of tooltips) {
    if ('type' in item && item.type === 'group') {
      const group = item as TooltipGroup;
      const stepIds: string[] = [];

      group.steps.forEach((step) => {
        stepIds.push(step.id);

        const mergedConfig: TooltipConfig = {
          title: step.title,
          description: step.description,
          placement: step.placement ?? 'top',
          placementByBreakpoint: step.placementByBreakpoint,
          maxWidth: step.maxWidth,
          delayMs: group.delayMs ?? 0,
          autoDismissMs: group.autoDismissMs,
          maxDisplayCount: group.maxDisplayCount ?? 2,
          showAfterVisitNumber: group.showAfterVisitNumber ?? 1,
          routes: group.routes ?? [],
          target: step.target ?? '',
          precedence: group.precedence ?? 0,
          displayAfterTooltipsIds: group.displayAfterTooltipsIds ?? [],
          variant: group.variant,
          presentation: group.presentation,
          multiStepGroupId: group.id,
          icon: step.icon,
          showBetaChip: step.showBetaChip,
          media: step.media,
          link: step.link,
          action: step.action,
          actionAlignment: step.actionAlignment,
          showHighlight: step.showHighlight ?? true,
          disableOnMobile: group.disableOnMobile,
          feature: group.feature ?? ONBOARDING_FEATURE_ID,
          audience: group.audience,
        };

        flattened.push({
          id: step.id,
          config: mergedConfig,
          groupId: group.id,
        });
      });

      multiStepGroups.set(group.id, stepIds);
    } else {
      const definition = item as TooltipDefinition;
      const config: TooltipConfig = {
        title: definition.title,
        description: definition.description,
        placement: definition.placement ?? 'top',
        placementByBreakpoint: definition.placementByBreakpoint,
        maxWidth: definition.maxWidth,
        delayMs: definition.delayMs ?? 0,
        autoDismissMs: definition.autoDismissMs,
        maxDisplayCount: definition.maxDisplayCount ?? 2,
        showAfterVisitNumber: definition.showAfterVisitNumber ?? 1,
        routes: definition.routes ?? [],
        target: definition.target ?? '',
        precedence: definition.precedence ?? 0,
        displayAfterTooltipsIds: definition.displayAfterTooltipsIds ?? [],
        variant: definition.variant,
        presentation: definition.presentation,
        icon: definition.icon,
        showBetaChip: definition.showBetaChip,
        media: definition.media,
        link: definition.link,
        action: definition.action,
        actionAlignment: definition.actionAlignment,
        showHighlight: definition.showHighlight ?? true,
        manualOpenOnly: definition.manualOpenOnly,
        feature: definition.feature ?? ONBOARDING_FEATURE_ID,
        disableOnMobile: definition.disableOnMobile,
        audience: definition.audience,
      };

      flattened.push({
        id: definition.id,
        config,
      });
    }
  }

  return { flattened, multiStepGroups };
}

class TooltipStore {
  private state: TooltipStoreState;

  private listeners = new Set<() => void>();

  private isInitializing = true;

  // Tooltips temporarily set aside via `releaseActive` (e.g. yielded to a
  // covering modal). Restoring one is a continuation of the same display, so
  // `confirmOpen` skips re-counting it against `maxDisplayCount`.
  private yieldedTooltipIds = new Set<string>();

  private retryTimeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

  // Consecutive `releaseActive` retries per tooltip, reset once one reaches the
  // screen. Bounds the retry loop for a target that never appears.
  private retryAttemptsById = new Map<string, number>();

  private updateState(
    updater: (state: TooltipStoreState) => TooltipStoreState
  ) {
    const next = updater(this.state);
    if (next !== this.state) {
      this.state = next;
      // Don't persist state during initialization to avoid overwriting localStorage
      if (!this.isInitializing) {
        this.persistState();
      }
      this.notifyListeners();
    }
  }

  constructor() {
    this.state = {
      tooltipsEnabled: false,
      enabledFeatures: {},
      audienceContext: {
        browserLanguage:
          typeof navigator !== 'undefined' ? navigator.language : '',
        clientName: null,
      },
      tooltips: new Map(),
      activeTooltipId: null,
      activeTooltipBypassEligibility: false,
      currentStepId: null,
      currentPathname: '',
      registrationOrderCounter: 0,
      registrationIndexById: new Map(),
      pendingRequests: new Map(),
      multiStepGroups: new Map(),
    };

    if (typeof window !== 'undefined') {
      // Initialize tooltips and load persisted state
      this.initializeTooltips();
      window.addEventListener('storage', this.handleStorageChange);
    }
  }

  private initializeTooltips() {
    // First, flatten tooltip groups and build multiStepGroups map
    const { flattened, multiStepGroups } = flattenTooltipGroups(TOOLTIPS);

    // Set multiStepGroups in state
    this.state = {
      ...this.state,
      multiStepGroups,
    };

    // Register all flattened tooltips
    flattened.forEach(({ id, config }) => {
      this.registerTooltip(id, config);
    });

    // Then, load and apply persisted state
    const raw = appComponentsStorage.get(STORAGE_KEY) || null;
    const persisted: PersistedTooltipState =
      (raw as PersistedTooltipState) || DEFAULT_PERSISTED_TOOLTIP_STATE;
    this.applyPersisted(persisted);

    // Initialization complete - enable persistence
    this.isInitializing = false;
  }

  private applyPersisted(persisted: PersistedTooltipState) {
    this.state.tooltipsEnabled = persisted.tooltipsEnabled;

    // Apply persisted data to already registered tooltips
    if (persisted.dismissedIds) {
      const nextTooltips = new Map(this.state.tooltips);
      persisted.dismissedIds.forEach((id) => {
        const tooltip = this.state.tooltips.get(id);
        if (tooltip) {
          nextTooltips.set(id, {
            ...tooltip,
            isDismissed: true,
            isOpen: false,
          });
        }
      });
      this.state = { ...this.state, tooltips: nextTooltips };
    }

    if (persisted.visitCounts) {
      const nextTooltips = new Map(this.state.tooltips);
      Object.entries(persisted.visitCounts).forEach(([id, count]) => {
        const tooltip = this.state.tooltips.get(id);
        if (tooltip) {
          nextTooltips.set(id, { ...tooltip, visitCount: count });
        }
      });
      this.state = { ...this.state, tooltips: nextTooltips };
    }

    if (persisted.displayCounts) {
      const nextTooltips = new Map(this.state.tooltips);
      Object.entries(persisted.displayCounts).forEach(([id, count]) => {
        const tooltip = this.state.tooltips.get(id);
        if (tooltip) {
          nextTooltips.set(id, { ...tooltip, displayCount: count });
        }
      });
      this.state = { ...this.state, tooltips: nextTooltips };
    }
  }

  private handleStorageChange = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      const raw = appComponentsStorage.get(STORAGE_KEY) || null;
      if (!raw) return;
      const persisted: PersistedTooltipState =
        (raw as PersistedTooltipState) || DEFAULT_PERSISTED_TOOLTIP_STATE;
      this.applyPersisted(persisted);
      this.notifyListeners();
    }
  };

  private persistState() {
    const { tooltipsEnabled, tooltips } = this.state;

    const dismissedIds: string[] = [];
    const visitCounts: Record<string, number> = {};
    const displayCounts: Record<string, number> = {};

    tooltips.forEach((tooltip, id) => {
      if (tooltip.isDismissed) dismissedIds.push(id);
      visitCounts[id] = tooltip.visitCount;
      displayCounts[id] = tooltip.displayCount;
    });

    appComponentsStorage.set(STORAGE_KEY, {
      tooltipsEnabled,
      dismissedIds,
      visitCounts,
      displayCounts,
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
    }
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  resetOnboardingState() {
    this.yieldedTooltipIds.clear();
    this.retryTimeoutIds.forEach((timeout) => clearTimeout(timeout));
    this.retryTimeoutIds.clear();
    this.retryAttemptsById.clear();
    this.updateState((state) => {
      const nextTooltips = new Map(state.tooltips);
      nextTooltips.forEach((t, id) => {
        const reset: TooltipState = {
          ...t,
          visitCount: 0,
          displayCount: 0,
          isDismissed: false,
          isOpen: false,
        };
        nextTooltips.set(id, reset);
      });

      return {
        ...state,
        tooltipsEnabled: false,
        tooltips: nextTooltips,
        activeTooltipId: null,
        activeTooltipBypassEligibility: false,
        currentStepId: null,
        currentPathname: '',
        pendingRequests: new Map(),
      };
    });
  }

  enableTooltips() {
    this.updateState((state) => {
      if (state.tooltipsEnabled) return state;
      return { ...state, tooltipsEnabled: true };
    });
  }

  disableTooltips() {
    this.updateState((state) => {
      if (!state.tooltipsEnabled) return state;
      return { ...state, tooltipsEnabled: false };
    });
  }

  isTooltipsEnabled(): boolean {
    return this.state.tooltipsEnabled;
  }

  setFeatureEnabled(feature: string, enabled: boolean) {
    this.updateState((state) => {
      const current = state.enabledFeatures[feature] === true;
      if (current === enabled) return state;
      const next = {
        ...state,
        enabledFeatures: { ...state.enabledFeatures, [feature]: enabled },
      };
      return applySelection(next);
    });
  }

  setAudienceContext(ctx: Partial<TooltipAudienceContext>) {
    this.updateState((state) => {
      const nextContext = { ...state.audienceContext, ...ctx };
      if (
        nextContext.browserLanguage === state.audienceContext.browserLanguage &&
        nextContext.clientName === state.audienceContext.clientName
      ) {
        return state;
      }
      return { ...state, audienceContext: nextContext };
    });

    const pathname = this.state.currentPathname;
    if (pathname) {
      this.enqueueReadyTooltipsForRoute(pathname);
    }
  }

  hasAnyFeatureEnabled(): boolean {
    if (this.state.tooltipsEnabled) return true;
    return Object.values(this.state.enabledFeatures).some(Boolean);
  }

  hasAnyTargetedFeatureEnabled(): boolean {
    return Object.values(this.state.enabledFeatures).some(Boolean);
  }

  registerTooltip(id: string, config: TooltipConfig) {
    this.updateState((state) => {
      if (state.tooltips.has(id)) return state;

      const normalized: TooltipConfig = {
        title: config.title,
        description: config.description,
        icon: config.icon,
        showBetaChip: config.showBetaChip,
        media: config.media,
        link: config.link,
        action: config.action,
        actionAlignment: config.actionAlignment,
        placement: config.placement ?? 'top',
        placementByBreakpoint: config.placementByBreakpoint,
        maxWidth:
          config.maxWidth ??
          (config.presentation === 'compact' ? undefined : '360px'),
        delayMs: config.delayMs ?? 0,
        autoDismissMs: config.autoDismissMs,
        maxDisplayCount: config.maxDisplayCount ?? 2,
        showAfterVisitNumber: config.showAfterVisitNumber ?? 1,
        routes: config.routes ?? [],
        target: config.target ?? '',
        showHighlight: config.showHighlight ?? true,
        precedence: config.precedence ?? 0,
        displayAfterTooltipsIds: config.displayAfterTooltipsIds ?? [],
        variant: config.variant,
        presentation: config.presentation ?? 'card',
        multiStepGroupId: config.multiStepGroupId,
        manualOpenOnly: config.manualOpenOnly,
        feature: config.feature ?? ONBOARDING_FEATURE_ID,
        disableOnMobile: config.disableOnMobile,
        audience: config.audience,
      };

      const nextTooltips = new Map(state.tooltips);
      nextTooltips.set(id, {
        id,
        config: normalized,
        visitCount: 0,
        displayCount: 0,
        isDismissed: false,
        isOpen: false,
      });

      const nextRegistrationIndexById = new Map(state.registrationIndexById);
      nextRegistrationIndexById.set(id, state.registrationOrderCounter);

      return {
        ...state,
        tooltips: nextTooltips,
        registrationOrderCounter: state.registrationOrderCounter + 1,
        registrationIndexById: nextRegistrationIndexById,
      };
    });
  }

  openTooltip(
    id: string,
    {
      bypassEligibility = false,
      contentOverrides,
    }: {
      bypassEligibility?: boolean;
      contentOverrides?: TooltipContentOverrides;
    } = {}
  ) {
    const tooltip = this.state.tooltips.get(id);
    if (!tooltip) return;

    if (contentOverrides) {
      this.updateState((state) => {
        const t = state.tooltips.get(id);
        if (!t) return state;
        const nextTooltips = new Map(state.tooltips);
        nextTooltips.set(id, { ...t, contentOverrides });
        return { ...state, tooltips: nextTooltips };
      });
    }

    this.enqueueTooltip(id, Boolean(bypassEligibility));
  }

  private enqueueTooltip(id: string, bypassEligibility: boolean) {
    this.updateState((state) => {
      const nextPending = new Map(state.pendingRequests);
      const existing = nextPending.get(id);
      const finalBypass = Boolean(
        bypassEligibility || existing?.bypassEligibility
      );
      nextPending.set(id, {
        bypassEligibility: finalBypass,
        retryAfter: finalBypass ? undefined : existing?.retryAfter,
      });
      return applySelection({ ...state, pendingRequests: nextPending });
    });
  }

  dismissTooltip(id: string, method: TooltipDismissalMethod = 'Completed') {
    const tooltip = this.state.tooltips.get(id);
    if (!tooltip) return;

    this.updateState((state) => {
      const t = state.tooltips.get(id);
      if (!t) return state;

      const nextTooltips = new Map(state.tooltips);
      const groupId = t.config.multiStepGroupId;
      const stepIdsToDismiss: string[] = [id];

      // If this is part of a multi-step group, dismiss all steps in the group
      if (groupId) {
        const stepIds = state.multiStepGroups.get(groupId);
        if (stepIds) {
          stepIdsToDismiss.push(...stepIds);
        }
      }

      // Dismiss all relevant tooltips
      stepIdsToDismiss.forEach((stepId) => {
        const stepTooltip = state.tooltips.get(stepId);
        if (stepTooltip) {
          nextTooltips.set(stepId, {
            ...stepTooltip,
            isDismissed: true,
            isOpen: false,
            lastDismissalMethod: method,
          });
        }
      });

      // Clear currentStepId if dismissing the active group
      const isDismissingActiveGroup =
        groupId &&
        state.currentStepId &&
        stepIdsToDismiss.includes(state.currentStepId);

      let nextStateBase: TooltipStoreState = {
        ...state,
        tooltips: nextTooltips,
        activeTooltipId: stepIdsToDismiss.includes(state.activeTooltipId ?? '')
          ? null
          : state.activeTooltipId,
        activeTooltipBypassEligibility: stepIdsToDismiss.includes(
          state.activeTooltipId ?? ''
        )
          ? false
          : state.activeTooltipBypassEligibility,
        currentStepId: isDismissingActiveGroup ? null : state.currentStepId,
      };

      const shouldDisable =
        areAllTooltipsDismissedIn(nextStateBase) &&
        nextStateBase.tooltipsEnabled;
      nextStateBase = shouldDisable
        ? { ...nextStateBase, tooltipsEnabled: false }
        : nextStateBase;

      return applySelection(nextStateBase);
    });
  }

  getActiveTooltip(): TooltipState | null {
    const id = this.state.activeTooltipId;
    if (!id) return null;
    return this.state.tooltips.get(id) ?? null;
  }

  getCurrentStepTooltip(): TooltipState | null {
    // For multi-step groups, return the current step
    const currentStepId = this.state.currentStepId;
    if (currentStepId) {
      return this.state.tooltips.get(currentStepId) ?? null;
    }
    // Fallback to active tooltip
    return this.getActiveTooltip();
  }

  getStepInfo(tooltipId: string): {
    current: number;
    total: number;
    groupId: string;
  } | null {
    const tooltip = this.state.tooltips.get(tooltipId);
    if (!tooltip?.config.multiStepGroupId) return null;

    const groupId = tooltip.config.multiStepGroupId;
    const stepIds = this.state.multiStepGroups.get(groupId);
    if (!stepIds || stepIds.length === 0) return null;

    // Use the tooltipId that's passed in to find its index in the group
    // This ensures we're getting step info for the tooltip that's actually being rendered
    const currentIndex = stepIds.indexOf(tooltipId);
    if (currentIndex === -1) return null;

    return {
      current: currentIndex,
      total: stepIds.length,
      groupId,
    };
  }

  nextStep(groupId: string, currentTooltipId?: string) {
    const stepIds = this.state.multiStepGroups.get(groupId);
    if (!stepIds || stepIds.length === 0) return;

    // Use provided currentTooltipId, or fall back to currentStepId from state
    const currentStepId =
      currentTooltipId ?? this.state.currentStepId ?? stepIds[0];
    if (!currentStepId) return;

    const currentIndex = stepIds.indexOf(currentStepId);
    if (currentIndex === -1 || currentIndex >= stepIds.length - 1) return;

    const nextStepId = stepIds[currentIndex + 1];
    const currentTooltip = this.state.tooltips.get(currentStepId);
    const nextTooltip = this.state.tooltips.get(nextStepId);
    if (!currentTooltip || !nextTooltip) return;

    this.updateState((state) => {
      const nextTooltips = new Map(state.tooltips);
      nextTooltips.set(currentStepId, { ...currentTooltip, isOpen: false });
      nextTooltips.set(nextStepId, { ...nextTooltip, isOpen: true });

      return {
        ...state,
        tooltips: nextTooltips,
        currentStepId: nextStepId,
        activeTooltipId: nextStepId,
      };
    });
  }

  previousStep(groupId: string, currentTooltipId?: string) {
    const stepIds = this.state.multiStepGroups.get(groupId);
    if (!stepIds || stepIds.length === 0) return;

    // Use provided currentTooltipId, or fall back to currentStepId from state
    const currentStepId =
      currentTooltipId ?? this.state.currentStepId ?? stepIds[0];
    if (!currentStepId) return;

    const currentIndex = stepIds.indexOf(currentStepId);
    if (currentIndex <= 0) return;

    const previousStepId = stepIds[currentIndex - 1];
    const currentTooltip = this.state.tooltips.get(currentStepId);
    const previousTooltip = this.state.tooltips.get(previousStepId);
    if (!currentTooltip || !previousTooltip) return;

    this.updateState((state) => {
      const nextTooltips = new Map(state.tooltips);
      nextTooltips.set(currentStepId, { ...currentTooltip, isOpen: false });
      nextTooltips.set(previousStepId, { ...previousTooltip, isOpen: true });

      return {
        ...state,
        tooltips: nextTooltips,
        currentStepId: previousStepId,
        activeTooltipId: previousStepId,
      };
    });
  }

  // Called when the renderer has confirmed that the tooltip target is visible.
  // Returns whether this counted as a *new* display (false when restoring a
  // yielded tooltip or when already open) so callers can gate display tracking.
  confirmOpen(id: string): boolean {
    let didCountNewDisplay = false;
    this.updateState((state) => {
      if (state.activeTooltipId !== id) return state;
      const currentTooltip = state.tooltips.get(id);
      if (!currentTooltip) return state;
      if (currentTooltip.isOpen) return state;

      // Reaching the screen clears the retry budget, so a later yield (e.g. to a
      // modal) gets a full set of attempts to come back.
      this.retryAttemptsById.delete(id);

      // A tooltip restored after a yield is the same display resuming, so don't
      // re-count it — otherwise repeated modal open/close burns maxDisplayCount.
      const wasYielded = this.yieldedTooltipIds.delete(id);
      didCountNewDisplay = !wasYielded;

      const nextTooltips = new Map(state.tooltips);
      const groupId = currentTooltip.config.multiStepGroupId;
      let currentStepId = state.currentStepId;

      // For multi-step groups, only increment displayCount once for the entire group (on first step)
      if (groupId) {
        const stepIds = state.multiStepGroups.get(groupId);
        if (stepIds && stepIds.length > 0) {
          // Ensure currentStepId is set to the tooltip being confirmed
          currentStepId = id;
          const firstStepId = stepIds[0];
          // Only increment if this is the first step
          if (didCountNewDisplay && id === firstStepId) {
            // Increment displayCount for all steps in the group
            stepIds.forEach((stepId) => {
              const stepTooltip = state.tooltips.get(stepId);
              if (stepTooltip) {
                nextTooltips.set(stepId, {
                  ...stepTooltip,
                  displayCount: stepTooltip.displayCount + 1,
                });
              }
            });
          }
        }
      } else if (didCountNewDisplay) {
        // Single tooltip: increment normally
        nextTooltips.set(id, {
          ...currentTooltip,
          displayCount: currentTooltip.displayCount + 1,
        });
      }

      // Mark tooltip as open, preserving any displayCount updates made above
      const tooltipToOpen = nextTooltips.get(id)!;
      nextTooltips.set(id, {
        ...tooltipToOpen,
        isOpen: true,
      });

      return { ...state, tooltips: nextTooltips, currentStepId };
    });
    return didCountNewDisplay;
  }

  // This is called when the renderer has confirmed that the tooltip target is not visible
  // and we should instead look for the next best tooltip
  releaseActive(id: string, { retry = true }: { retry?: boolean } = {}) {
    let didRelease = false;
    // A selector that never resolves would otherwise re-select, re-mount a
    // document-wide MutationObserver, and expire again on a loop. Give up after
    // a bounded number of attempts and leave the request dropped rather than
    // cycling; a later explicit request or route match can still enqueue it.
    const attempt = (this.retryAttemptsById.get(id) ?? 0) + 1;
    const shouldRetry = retry && attempt <= MAX_RELEASE_RETRY_ATTEMPTS;

    this.updateState((state) => {
      if (state.activeTooltipId !== id) return state;
      didRelease = true;
      const t = state.tooltips.get(id);
      // Only a tooltip that reached the screen is resuming the same display.
      if (t?.isOpen) this.yieldedTooltipIds.add(id);

      const nextTooltips = new Map(state.tooltips);
      if (t) {
        // Ensure it is not marked open while being released
        nextTooltips.set(id, { ...t, isOpen: false });
      }

      const nextPending = new Map(state.pendingRequests);
      if (shouldRetry) {
        // set a short cooldown to avoid immediate reselection
        nextPending.set(id, {
          bypassEligibility: state.activeTooltipBypassEligibility,
          retryAfter: Date.now() + RETRY_COOLDOWN_MS,
        });
      } else {
        nextPending.delete(id);
      }

      const base: TooltipStoreState = {
        ...state,
        tooltips: nextTooltips,
        pendingRequests: nextPending,
        activeTooltipId: null,
        activeTooltipBypassEligibility: false,
      };
      return applySelection(base);
    });

    if (!didRelease) return;

    if (!shouldRetry) {
      this.retryAttemptsById.delete(id);
      return;
    }

    this.retryAttemptsById.set(id, attempt);
    clearTimeout(this.retryTimeoutIds.get(id));
    this.retryTimeoutIds.set(
      id,
      setTimeout(() => {
        this.retryTimeoutIds.delete(id);
        this.updateState((state) => {
          const request = state.pendingRequests.get(id);
          if (!request) return state;
          const nextPending = new Map(state.pendingRequests);
          nextPending.set(id, { ...request, retryAfter: undefined });
          return applySelection({ ...state, pendingRequests: nextPending });
        });
      }, RETRY_COOLDOWN_MS)
    );
  }

  cancelRequest(id: string) {
    clearTimeout(this.retryTimeoutIds.get(id));
    this.retryTimeoutIds.delete(id);
    this.retryAttemptsById.delete(id);
    this.yieldedTooltipIds.delete(id);
    this.updateState((state) => {
      const isActive = state.activeTooltipId === id;
      if (!isActive && !state.pendingRequests.has(id)) return state;

      const nextPending = new Map(state.pendingRequests);
      nextPending.delete(id);
      const nextTooltips = new Map(state.tooltips);
      const tooltip = nextTooltips.get(id);
      if (tooltip?.isOpen) {
        nextTooltips.set(id, { ...tooltip, isOpen: false });
      }
      return applySelection({
        ...state,
        tooltips: nextTooltips,
        pendingRequests: nextPending,
        activeTooltipId: isActive ? null : state.activeTooltipId,
        activeTooltipBypassEligibility: isActive
          ? false
          : state.activeTooltipBypassEligibility,
      });
    });
  }

  /**
   * Re-run selection after whatever was blocking pending tooltips (e.g. a
   * covering modal) is gone. Unlike `releaseActive`, this doesn't depend on a
   * particular tooltip still being active — a yielded tooltip can otherwise sit
   * in `pendingRequests` with no later event to reselect it. Clears the
   * anti-thrash cooldown too, since the blocker it guarded against is gone.
   */
  reconsiderPending() {
    this.updateState((state) => {
      if (state.activeTooltipId || state.pendingRequests.size === 0) {
        return state;
      }
      const nextPending = new Map(state.pendingRequests);
      for (const [id, req] of nextPending) {
        nextPending.set(id, { ...req, retryAfter: undefined });
      }
      return applySelection({ ...state, pendingRequests: nextPending });
    });
  }

  getAllTooltips(): TooltipState[] {
    return Array.from(this.state.tooltips.values());
  }

  /** Whether a queued tooltip could be selected now if the active one yielded. */
  hasSelectablePendingTooltip(): boolean {
    const now = Date.now();
    for (const [id, req] of this.state.pendingRequests.entries()) {
      const onCooldown = req.retryAfter != null && now < req.retryAfter;
      const t = this.state.tooltips.get(id);
      if (
        !onCooldown &&
        t &&
        selectIsEligible(this.state, t, {
          bypassEligibility: req.bypassEligibility,
        })
      ) {
        return true;
      }
    }
    return false;
  }

  getOpenTooltipForRoute(pathname: string): TooltipState | null {
    // For multi-step groups, prefer the current step
    const currentStepId = this.state.currentStepId;
    if (currentStepId) {
      const currentStep = this.state.tooltips.get(currentStepId);
      if (
        currentStep &&
        currentStep.isOpen &&
        !currentStep.isDismissed &&
        isConfigEligibleForRoute(currentStep.config.routes, pathname)
      ) {
        return currentStep;
      }
    }

    // Prefer the active tooltip if it is open and eligible
    const activeId = this.state.activeTooltipId;
    if (activeId) {
      const t = this.state.tooltips.get(activeId);
      if (
        t &&
        t.isOpen &&
        !t.isDismissed &&
        isConfigEligibleForRoute(t.config.routes, pathname)
      ) {
        return t;
      }
    }

    // Fallback: return the first open and eligible tooltip (should not happen if invariant holds)
    for (const [, t] of this.state.tooltips.entries()) {
      if (
        t.isOpen &&
        !t.isDismissed &&
        isConfigEligibleForRoute(t.config.routes, pathname)
      ) {
        return t;
      }
    }
    return null;
  }

  openReadyTooltipsForRoute(pathname: string) {
    // Avoid duplicate visit increments when called repeatedly for the same route
    // (e.g., React Strict Mode double-invokes effects in dev)
    if (this.state.currentPathname === pathname) {
      return;
    }

    // Update state (increment visits for eligible tooltips)
    this.updateState((state) => {
      const nextTooltips = new Map(state.tooltips);
      nextTooltips.forEach((t, id) => {
        const isEligiblePath =
          !t.config.routes || t.config.routes.length === 0
            ? true
            : isConfigEligibleForRoute(t.config.routes, pathname);
        if (
          isFeatureEnabled(state, t.config.feature) &&
          !t.isDismissed &&
          !t.isOpen &&
          !t.config.manualOpenOnly &&
          isEligiblePath
        ) {
          nextTooltips.set(id, { ...t, visitCount: t.visitCount + 1 });
        }
      });

      return {
        ...state,
        currentPathname: pathname,
        tooltips: nextTooltips,
      };
    });

    this.enqueueReadyTooltipsForRoute(pathname);
  }

  private enqueueReadyTooltipsForRoute(pathname: string) {
    const ready = findAllReadyTooltipsForRoute(this.state, pathname);
    if (ready.length > 0) {
      // Enqueue all ready tooltips in sorted order; applySelection will pick the best
      ready.forEach(({ id }) => {
        this.enqueueTooltip(id, false);
      });
    }
  }
}

export const tooltipStore = new TooltipStore();

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    tooltipStore?: typeof tooltipStore;
  }
}

if (typeof window !== 'undefined') {
  const envName = CONFIG.name;
  if (
    envName === 'DEV_APP_SERVER' ||
    envName === 'STAGING_APP_SERVER' ||
    envName === 'PREVIEW_APP_SERVER' ||
    envName === 'PREVIEW_APP_STAGING_SERVER'
  ) {
    window.tooltipStore = tooltipStore;
  }
}
